import { type ApolloQueryResult } from '@apollo/client';
import { Button } from 'antd';
import { useState, useEffect, type Dispatch, type SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type GetVehicleQuery, type GetVehicleQueryVariables } from '../../../api/queries/getVehicle';
import { ApplicationStage } from '../../../api/types';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import FinderVehicleApplicationList from './FinderVehicleApplicationList';
import { HeaderTabs } from './Inner';
import LtaDetails from './LtaDetails';
import MainDetails from './MainDetails';
import VehicleDetails from './VehicleDetails';

type FinderVehicleDetailsProps = {
    disabled?: boolean;
    isPartnerNumberSetup: boolean;
    hasLTA: boolean;
    refetch: (variables?: Partial<GetVehicleQueryVariables>) => Promise<ApolloQueryResult<GetVehicleQuery>>;
    finderVehicleVin: string;
    activeTab: HeaderTabs;
    setSelectedApplications: Dispatch<SetStateAction<string[]>>;
    selectedApplications: string[];
    openLtaDrawer: () => void;
};

const FinderVehicleDetails = ({
    disabled,
    isPartnerNumberSetup,
    refetch,
    hasLTA,
    finderVehicleVin,
    activeTab,
    setSelectedApplications,
    selectedApplications,
    openLtaDrawer,
}: FinderVehicleDetailsProps) => {
    const { t } = useTranslation('finderVehicle');

    const [activeKeys, setActiveKeys] = useState(['mainDetails', 'vehicleDetails', hasLTA ? 'ltaDetails' : '']);

    useEffect(() => {
        if (hasLTA && !activeKeys.includes('ltaDetails')) {
            setActiveKeys([...activeKeys, 'ltaDetails']);
        }
    }, [hasLTA, activeKeys]);

    const handlePanelChange = (keys: string[]) => {
        setActiveKeys(keys);
    };

    const renderActiveTab = useMemo(() => {
        switch (activeTab) {
            case HeaderTabs.VehicleDetails: {
                return (
                    <CollapsibleWrapper activeKey={activeKeys} onChange={handlePanelChange}>
                        <Panel key="mainDetails" header={t('finderVehicle:cardTitles.mainDetails')}>
                            <MainDetails disabled={disabled} hasLTA={hasLTA} refetch={refetch} />
                        </Panel>
                        <Panel key="vehicleDetails" header={t('finderVehicle:cardTitles.vehicleDetails')}>
                            <VehicleDetails isPartnerNumberSetup={isPartnerNumberSetup} />
                        </Panel>
                        {hasLTA && (
                            <Panel
                                key="ltaDetails"
                                extra={
                                    !disabled && (
                                        <Button key="finderVehicleAddLta" onClick={openLtaDrawer} type="primary">
                                            {t('finderVehicle:actions.addLta')}
                                        </Button>
                                    )
                                }
                                header={t('finderVehicle:cardTitles.ltaDetails')}
                            >
                                <LtaDetails disabled={disabled} />
                            </Panel>
                        )}
                    </CollapsibleWrapper>
                );
            }

            case HeaderTabs.Financing: {
                return (
                    <FinderVehicleApplicationList
                        finderVehicleVin={finderVehicleVin}
                        setSelectedApplications={setSelectedApplications}
                        stage={ApplicationStage.Financing}
                    />
                );
            }

            case HeaderTabs.Appointment: {
                return (
                    <FinderVehicleApplicationList
                        finderVehicleVin={finderVehicleVin}
                        setSelectedApplications={setSelectedApplications}
                        stage={ApplicationStage.Appointment}
                    />
                );
            }

            case HeaderTabs.Lead: {
                return (
                    <FinderVehicleApplicationList
                        finderVehicleVin={finderVehicleVin}
                        setSelectedApplications={setSelectedApplications}
                        stage={ApplicationStage.Lead}
                    />
                );
            }

            case HeaderTabs.Reservations: {
                return (
                    <FinderVehicleApplicationList
                        finderVehicleVin={finderVehicleVin}
                        setSelectedApplications={setSelectedApplications}
                        stage={ApplicationStage.Reservation}
                    />
                );
            }

            case HeaderTabs.Insurance: {
                return (
                    <FinderVehicleApplicationList
                        finderVehicleVin={finderVehicleVin}
                        setSelectedApplications={setSelectedApplications}
                        stage={ApplicationStage.Insurance}
                    />
                );
            }

            default:
                return null;
        }
    }, [
        activeKeys,
        activeTab,
        disabled,
        finderVehicleVin,
        hasLTA,
        isPartnerNumberSetup,
        openLtaDrawer,
        refetch,
        setSelectedApplications,
        t,
    ]);

    return renderActiveTab;
};

export default FinderVehicleDetails;
